// src/utils/stringUtils.js
// ============================================================
// STRING UTILITIES - Common string manipulation functions
// ============================================================
// Centralizes repeated string operations to reduce code duplication
// and improve consistency across the codebase.
// ============================================================

/**
 * Normalizes a string for comparison by converting to lowercase and trimming
 * This pattern was repeated 20+ times across App.js and dashboard
 * @param {any} value - Value to normalize (handles null/undefined)
 * @returns {string} Normalized lowercase trimmed string
 * @example
 * normalizeString('  On Tap  ') // 'on tap'
 * normalizeString(null) // ''
 * normalizeString(123) // '123'
 */
export const normalizeString = (value) => {
  return (value || '').toString().trim().toLowerCase();
};

/**
 * Compares two strings for equality after normalization
 * @param {any} a - First value to compare
 * @param {any} b - Second value to compare
 * @returns {boolean} True if normalized strings are equal
 * @example
 * stringsMatch('On Tap', 'on tap') // true
 * stringsMatch('', null) // true
 */
export const stringsMatch = (a, b) => {
  return normalizeString(a) === normalizeString(b);
};

/**
 * Checks if a normalized string is empty or 'unassigned'
 * Useful for location filtering where empty and 'unassigned' are equivalent
 * @param {any} value - Value to check
 * @returns {boolean} True if empty or 'unassigned'
 */
export const isUnassigned = (value) => {
  const normalized = normalizeString(value);
  return normalized === '' || normalized === 'unassigned';
};

/**
 * Checks if a value matches a filter, handling 'all' and 'unassigned' special cases
 * @param {any} value - The value to check
 * @param {any} filter - The filter to match against
 * @returns {boolean} True if value matches filter
 */
export const matchesLocationFilter = (value, filter) => {
  const filterNorm = normalizeString(filter);
  
  // 'all' filter matches everything
  if (filterNorm === 'all') return true;
  
  // 'unassigned' filter matches empty or unassigned values
  if (filterNorm === 'unassigned') {
    return isUnassigned(value);
  }
  
  // Standard comparison
  return stringsMatch(value, filter);
};
